import { useIntegrationEhrInfo } from '@/hooks/integrations/useIntegrationEhrInfo';
import { useOldFrameworkIntegrations } from '@/hooks/integrations/useOldFrameworkIntegrations';
import { useQueryKindeProperties } from '@/hooks/query/useQueryKindeProperties';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { getIntegrationLinkedSessionInfo } from '@/pages/api/scribeAPIV2';
import { type EHRProvider } from '@heidi/schema/ml-scribe';
import { useQuerySessionById } from '@heidi/session/data';
import { skipToken, useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { GET_INTEGRATION_LINK_SESSION_INFO } from './integrationLinkSessionInfoKeys';

export { GET_INTEGRATION_LINK_SESSION_INFO } from './integrationLinkSessionInfoKeys';

export function useQueryIntegrationLinkSessionInfo({ sessionId }: { sessionId: string }) {
  const { isAuthenticated } = useIsAuthenticated();
  const { data: kindeProperties } = useQueryKindeProperties();
  const user = useQueryUser((data) => data.kinde_user_id);
  const oldFrameworkIntegrations = useOldFrameworkIntegrations();

  const { isIntegrationOnboardingSession } = useIntegrationEhrInfo();
  const { data: sessionPatientEhrProvider } = useQuerySessionById({
    sessionId: sessionId,
    select: (data) => data?.patient_profile?.ehr,
  });

  const isQueryable = Boolean(
    isAuthenticated &&
    kindeProperties?.region &&
    user &&
    isIntegrationOnboardingSession &&
    sessionId &&
    !oldFrameworkIntegrations.includes(sessionPatientEhrProvider as EHRProvider)
  );

  return useQuery({
    queryKey: [GET_INTEGRATION_LINK_SESSION_INFO, { sessionId }],
    retry: false,
    queryFn: isQueryable
      ? async () => {
          try {
            const { data } = await getIntegrationLinkedSessionInfo({ sessionId });
            return data;
          } catch (error) {
            // 422 means "no integration link exists for this session" —
            // cache as a successful null so the query doesn't refire on every
            // subscriber remount / window focus.
            if (isAxiosError(error) && error.response?.status === 422) {
              return null;
            }
            throw error;
          }
        }
      : skipToken,
  });
}

const GET_LINKED_SESSION_INFO_BY_CASE_ID = 'GetLinkedSessionInfoByCaseId';

export const useQueryIntegrationLinkSessionInfoByCaseId = ({
  caseId,
}: {
  caseId?: string;
}) => {
  const { isAuthenticated } = useIsAuthenticated();
  const { data: kindeProperties } = useQueryKindeProperties();
  const user = useQueryUser((data) => data.kinde_user_id);

  return useQuery({
    queryKey: [GET_LINKED_SESSION_INFO_BY_CASE_ID, { caseId }],
    retry: false,
    queryFn: Boolean(isAuthenticated && kindeProperties?.region && user && caseId)
      ? async () => {
          const { data } = await getIntegrationLinkedSessionInfo({
            provider_unique_identifier: caseId,
          });
          return data;
        }
      : skipToken,
  });
};
